import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { GripVertical, Pencil, Check, X, Trash2, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { RevisionReasonCategoryConfig } from '@/types/resource-budget-settings.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RevisionReasonCategoryManagerProps {
  /** Full list of categories (enabled + disabled), sorted by displayOrder */
  categories: RevisionReasonCategoryConfig[];
  /** Called when a category's isEnabled flag is toggled */
  onToggle: (id: string) => void;
  /** Called with the new ordered array of ids after a drag-reorder */
  onReorder: (orderedIds: string[]) => void;
  /** Called when the label of a custom category is updated */
  onRenameCustom?: (id: string, newLabel: string) => void;
  /** Called when a custom category is permanently deleted */
  onDelete?: (id: string) => void;
  /** Called when the "Add Category" button is clicked */
  onAddClick: () => void;
}

// ---------------------------------------------------------------------------
// Sortable row sub-component
// ---------------------------------------------------------------------------

interface SortableCategoryRowProps {
  category: RevisionReasonCategoryConfig;
  onToggle: (id: string) => void;
  onRenameCustom?: (id: string, newLabel: string) => void;
  onDelete?: (id: string) => void;
  isDragging?: boolean;
}

function SortableCategoryRow({
  category,
  onToggle,
  onRenameCustom,
  onDelete,
  isDragging = false,
}: SortableCategoryRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(category.label);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleEditConfirm = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== category.label && onRenameCustom) {
      onRenameCustom(category.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditValue(category.label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleEditConfirm();
    if (e.key === 'Escape') handleEditCancel();
  };

  const handleDeleteConfirm = () => {
    onDelete?.(category.id);
    setConfirmingDelete(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-background hover:bg-muted/40 transition-colors group"
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing focus:outline-none"
        aria-label="Drag to reorder"
        tabIndex={-1}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Label / edit input / delete confirmation */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={50}
              autoFocus
              className="h-7 text-sm"
            />
            <Button size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" onClick={handleEditConfirm}>
              <Check className="h-3.5 w-3.5 text-success-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" onClick={handleEditCancel}>
              <X className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        ) : confirmingDelete ? (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
            <span className="text-sm text-destructive">
              Delete <span className="font-medium">"{category.label}"</span>? This cannot be undone.
            </span>
            <Button size="sm" variant="destructive" className="h-6 px-2 text-xs ml-1" onClick={handleDeleteConfirm}>
              Delete
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <span className={`text-sm font-medium truncate ${!category.isEnabled ? 'text-muted-foreground line-through' : ''}`}>
            {category.label}
          </span>
        )}
      </div>

      {/* Actions — always visible for custom categories */}
      {!confirmingDelete && !isEditing && category.isCustom && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => setIsEditing(true)}
                aria-label={`Rename ${category.label}`}
              >
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Rename</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => setConfirmingDelete(true)}
                aria-label={`Delete ${category.label}`}
              >
                <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Enable / disable toggle — always visible */}
      {!confirmingDelete && !isEditing && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex flex-shrink-0">
              <Switch
                checked={category.isEnabled}
                onCheckedChange={() => onToggle(category.id)}
                aria-label={`${category.isEnabled ? 'Disable' : 'Enable'} ${category.label}`}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {category.isEnabled ? 'Hide from revision dropdown' : 'Show in revision dropdown'}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag overlay row (ghost shown while dragging)
// ---------------------------------------------------------------------------

function DragOverlayRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-background shadow-lg opacity-95">
      <GripVertical className="h-4 w-4 text-muted-foreground/50" />
      <span className="text-sm font-medium flex-1">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RevisionReasonCategoryManager({
  categories,
  onToggle,
  onReorder,
  onRenameCustom,
  onDelete,
  onAddClick,
}: RevisionReasonCategoryManagerProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(categories, oldIndex, newIndex);
      onReorder(reordered.map((c) => c.id));
    }
  };

  const activeDragCategory = activeDragId
    ? categories.find((c) => c.id === activeDragId)
    : null;

  const categoryIds = categories.map((c) => c.id);

  return (
    <div className="space-y-2">
      {categories.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No categories configured.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {categories.map((category) => (
                <SortableCategoryRow
                  key={category.id}
                  category={category}
                  onToggle={onToggle}
                  onRenameCustom={onRenameCustom}
                  onDelete={onDelete}
                  isDragging={activeDragId === category.id}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeDragCategory ? (
              <DragOverlayRow label={activeDragCategory.label} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add category CTA */}
      <div className="pt-2">
        <Button variant="outline" size="sm" onClick={onAddClick} className="w-full">
          + Add Reason
        </Button>
      </div>
    </div>
  );
}
